'use client'
import { useEffect, useRef } from 'react'
import { animate, useInView, useMotionValue, useReducedMotion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { BlurIn, FadeInUp } from '@/components/ui/FadeInUp'
import { GRADIENT_TEXT_BLUE } from '@/lib/styles'
import { measurementSteps } from '@/lib/data/measurement'
import ProofChart from './ProofChart'
import TimelineStep from './TimelineStep'

// Where each step lights up along the shared 0→1 timeline.
const THRESHOLDS = [0.12, 0.4, 0.68, 0.95]

// Decorative 4-point sparkle — echoes the Trend brand mark; uses the shared
// `twinkle` keyframe from globals.css.
function Sparkle({ className, size = 18 }: { className: string; size?: number }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden
      className={`absolute ${className}`}
      style={{ animation: 'twinkle 3.2s ease-in-out infinite' }}
    >
      <path
        d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z"
        fill="rgba(0,180,216,0.55)"
      />
    </svg>
  )
}

export default function Measurement() {
  const { t, lang } = useLanguage()
  const m = t.measurement
  const fa = lang === 'fa'

  // One shared timeline drives both the step nodes and the chart, so they
  // can never drift out of sync. It plays once when the section scrolls in.
  const progress = useMotionValue(0)
  const ref = useRef<HTMLDivElement>(null)
  const inView = useInView(ref, { once: true, margin: '-80px' })
  const reduce = useReducedMotion()

  useEffect(() => {
    if (!inView) return
    if (reduce) {
      progress.set(1)
      return
    }
    const controls = animate(progress, 1, { duration: 3.4, ease: 'linear' })
    return () => controls.stop()
  }, [inView, reduce, progress])

  return (
    <section
      id="measurement"
      data-nav-dark
      className="py-16 sm:py-24 md:py-32 border-b border-white/5"
      style={{ background: '#0b1629' }}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        {/* Header */}
        <BlurIn>
          <p
            className="text-[11px] font-bold uppercase tracking-[0.28em] mb-6"
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            {m.section}
          </p>
        </BlurIn>
        <BlurIn delay={0.07}>
          <h2 className="font-display font-black text-2xl sm:text-3xl md:text-5xl leading-[1.12] text-white max-w-3xl">
            {m.headingPre}
            <span style={GRADIENT_TEXT_BLUE}>{m.headingAccent}</span>
          </h2>
        </BlurIn>
        <FadeInUp delay={0.1}>
          <p
            className="text-sm md:text-base leading-loose max-w-2xl mt-5"
            style={{ color: 'rgba(255,255,255,0.45)' }}
          >
            {m.sub}
          </p>
        </FadeInUp>

        {/* Two columns: synced checkmark timeline + proof chart */}
        <div
          ref={ref}
          className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-stretch mt-14 md:mt-20"
        >
          {/* Step timeline */}
          <FadeInUp className="flex flex-col">
            {measurementSteps.map((step, i) => (
              <TimelineStep
                key={step.id}
                step={step}
                progress={progress}
                threshold={THRESHOLDS[i]}
                nextThreshold={THRESHOLDS[i + 1] ?? 1}
                isLast={i === measurementSteps.length - 1}
                fa={fa}
              />
            ))}
          </FadeInUp>

          {/* Proof chart — stretched to match the timeline column height */}
          <FadeInUp delay={0.05} className="h-full">
            <div className="relative flex flex-col h-full">
              <Sparkle className="-top-3 left-2 sm:left-6" size={20} />
              <Sparkle className="top-1/3 -right-1" size={13} />
              <div
                className="relative flex-1 rounded-3xl border border-white/8 overflow-hidden p-5 sm:p-7"
                style={{ background: 'rgba(255,255,255,0.02)' }}
              >
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      'radial-gradient(ellipse 60% 70% at 72% 18%, rgba(0,180,216,0.15), transparent 70%)',
                  }}
                />
                <div className="relative h-full">
                  <ProofChart progress={progress} labels={m.chart} fa={fa} />
                </div>
              </div>
              <p
                className="text-[11px] mt-3 text-center"
                style={{ color: 'rgba(255,255,255,0.3)' }}
              >
                {m.chart.caption}
              </p>
            </div>
          </FadeInUp>
        </div>
      </div>
    </section>
  )
}
